import { Box, Button, Skeleton, Stack, Typography } from "@mui/material";
import { AppModal } from "global/components/AppModal";
import strings from "global/constants/StringConstants";
import { useAppSelector } from "hooks/use-app-select";
import { hasAccessTo } from "layouts/Config";
import { useState } from "react";
import moment from "moment";
import { cancelSubscription } from "../MyPlanFunctions";
import { convertESTtoUserLocalTime, isUnlimitedLimit } from "helpers/methods";
import { isTrial } from "redux/authSlice";
import myPlanTokens, { myPlanResponsiveTokens } from "../MyPlan.tokens";
import {
  accentButtonSx,
  cancelledBannerSx,
  cancelledNoteSx,
  cancelledTitleSx,
  creditAmountSx,
  creditBannerSx,
  creditNoteSx,
  ghostButtonSx,
  noPlanBodySx,
  noPlanTitleSx,
  pillSx,
  planCardSx,
  planActionPrimarySx,
  planNameSx,
  sectionDescSx,
  statCaptionSx,
  statCellSx,
  statLabelSx,
  statUnitSx,
  statValueSx,
} from "../MyPlan.styles";
import { formatPlanAmount } from "../components/MyPlanHelper";
import useSeatContext from "hooks/use-seat-context";
import useBillingSummary from "hooks/use-billing-summary";
import {
  formatMoney,
  getBilledSeatCount,
  getPlanSeatLimit,
  getSeatReductionFromError,
  getSeatUsage,
  isKeepSelectionRejected,
  needsSeatReduction,
} from "helpers/perSeatHelpers";
import {
  SeatReductionRequired,
  SubscriptionState,
  subscriptionState as subscriptionStates,
} from "types/perSeat";
import { fetchSeatCheck, updateSeatCount } from "Services/CommonServices";
import {
  handleErrorMessage,
  openErrorNotification,
  openSuccessNotification,
} from "helpers/methods";
import notifiers from "global/constants/NotificationConstants";
import ManageSeatsPanel from "./components/ManageSeatsPanel/ManageSeatsPanel";
import CustomSeatReductionDialog from "global/components/CustomSeatReductionDialog/CustomSeatReductionDialog";

const t = myPlanTokens;
const r = myPlanResponsiveTokens;

const PlanDetails = ({
  isLoading,
  setLoading,
  getUserData,
  currentPlan,
  setUpgradePlanScreen,
  accountStats,
  isSubscriptionActive,
  subscriptionState,
}: {
  isLoading: boolean;
  setLoading: Function;
  getUserData: any;
  currentPlan: any;
  setUpgradePlanScreen: Function;
  accountStats: any;
  isSubscriptionActive: any;
  subscriptionState?: SubscriptionState | null;
  isMobile?: boolean;
}) => {
  const isTrialAccount = useAppSelector(isTrial);
  const [cancelSubscriptionPopup, setCancelSubscriptionPopup] =
    useState<boolean>(false);
  // Whether the plan is sold by the seat; only the plan catalog carries the
  // `perSeatBilling` metadata that decides it.
  // Seats are bought and released from the Team screen, which is where the
  // people filling them live; this screen only reports what's owned.
  const {
    isPerSeat,
    catalogPlan,
    refresh: refreshSeats,
  } = useSeatContext(currentPlan);
  // Gross cost, saved credit and the net due next renewal. Only paid accounts
  // have any of that, so the call is held back until the plan is active.
  const {
    summary: billingSummary,
    isLoading: isCreditLoading,
    refresh: refreshBilling,
  } = useBillingSummary(Boolean(isSubscriptionActive));
  /** Buying or releasing seats — the in-plan money move, post-purchase. */
  const [isManagingSeats, setManagingSeats] = useState<boolean>(false);
  const [isSeatUpdateRunning, setSeatUpdateRunning] = useState<boolean>(false);
  const [seatReduction, setSeatReduction] =
    useState<SeatReductionRequired | null>(null);
  const [pendingSeatCount, setPendingSeatCount] = useState<number>(0);

  const isPlanExpired = accountStats?.validTill
    ? new Date(accountStats.validTill) < new Date()
    : true;

  const certifiedSignedStats = accountStats?.appPlanLimitList?.find(
    (plan: any) => plan.entity === strings.notariusVerifiedEnvelope,
  );

  const getPeriodLabel = (days: number): string => {
    switch (days) {
      case 30:
        return "/mo";
      case 365:
      case 366:
        return "/yr";
      default:
        /*
          The backend spells "never expires" as a sentinel validity (99999
          days), the same way it spells an unlimited allowance — so a lifetime
          plan must read "forever" rather than quoting the sentinel back. A
          genuine multi-year plan still shows its real day count.
        */
        return isUnlimitedLimit(days) ? " forever" : ` / ${days} days`;
    }
  };

  const getBillingCycleLabel = (days: number): string | null => {
    if (days === 30) return "Monthly";
    if (days === 365 || days === 366) return "Annual";
    if (days > 366) return "Forever";
    return null;
  };

  const handleClose = () => {
    setCancelSubscriptionPopup(false);
  };

  /**
   * The picker's roster went stale — someone was deactivated or removed in
   * another session while it was open — so the ids no longer match who is
   * billable. Fetch it again and put the customer back in the picker instead of
   * leaving them with a bare error and a change that never happened.
   */
  const reopenKeepPicker = async (count: number, message?: string) => {
    openErrorNotification(message ?? notifiers.GENERIC_ERROR);
    try {
      const check = await fetchSeatCheck(currentPlan?.id, count);
      await refreshSeats();
      if (needsSeatReduction(check)) {
        setPendingSeatCount(count);
        setSeatReduction(check);
        return;
      }
      // Enough seats freed up in the meantime that nobody has to be dropped;
      // the customer can simply run the change again.
      setSeatReduction(null);
    } catch {
      // Nothing trustworthy to re-open the picker with. The message above
      // already said what happened.
      setSeatReduction(null);
    }
  };

  /**
   * Change the seats the account pays for. An increase charges the prorated
   * difference now, a decrease credits the unused part to the balance; asking
   * for fewer seats than there are active users answers `409` with the people
   * to choose between, so the keep-picker resends the same count with the ids.
   */
  const runSeatUpdate = async (count: number, keepUserIds?: number[]) => {
    try {
      setSeatUpdateRunning(true);
      await updateSeatCount(count, keepUserIds);
      openSuccessNotification(
        `You now have ${count} seat${count === 1 ? "" : "s"}.`,
      );
      setManagingSeats(false);
      setSeatReduction(null);
      // Each refetch drives its own loading flag (the plan card's, the seats
      // meter's, the credit banner's) — run them together so none holds
      // another's data back from showing the moment it lands.
      await Promise.all([getUserData?.(), refreshSeats(), refreshBilling()]);
    } catch (error: any) {
      const reduction = getSeatReductionFromError(error);
      setManagingSeats(false);
      if (reduction) {
        setPendingSeatCount(count);
        setSeatReduction(reduction);
        return;
      }
      // A 406 answering a selection is about that selection going stale, not
      // about the account being full — it needs a fresh roster, not an error.
      if (isKeepSelectionRejected(error, keepUserIds)) {
        await reopenKeepPicker(count, error?.message);
        return;
      }
      handleErrorMessage(error);
    } finally {
      setSeatUpdateRunning(false);
    }
  };

  /**
   * The refund line is the one bullet that depends on the plan: annual plans
   * refund the unused portion, monthly ones don't. There's no data on this
   * screen (or anywhere in the account/plan/subscription-state responses) to
   * tell a customer still inside a new-subscriber refund window from one who
   * isn't, so that case is left out rather than guessed at.
   */  const cancelSubsPopupBody = () => {
    const days = currentPlan?.validityInDays;
    const isAnnualPlan = days === 365 || days === 366;
    return (
      <Stack spacing={2}>
        <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
          <Typography component="li" variant="body2" sx={{ mb: 1 }}>
            You’ll immediately move to the Lifetime Free plan — all other team
            members will be deactivated except you.
          </Typography>
          <Typography component="li" variant="body2" sx={{ mb: 1 }}>
            {isAnnualPlan
              ? "You’ll get a prorated refund for unused time."
              : "No refund for the current period."}
          </Typography>
          <Typography component="li" variant="body2" sx={{ mb: 1 }}>
            Any unused account credit will be forfeited and can’t be recovered
            later.
          </Typography>
          <Typography component="li" variant="body2">
            Your documents, contacts, and templates stay accessible.
          </Typography>
        </Box>
        <Typography variant="body2" fontWeight={600}>
          This takes effect right away and can’t be undone.
        </Typography>
      </Stack>    );
  };

  const getNoActivePlanBox = () => {
    return (
      <Stack
        alignItems="center"
        justifyContent="center"
        textAlign="center"
        spacing={2.5}
        sx={{ px: t.space.gutter, py: "44px" }}
      >
        <Stack spacing={1} sx={{ maxWidth: "460px" }}>
          <Box component="h2" sx={noPlanTitleSx}>
            No Active Subscription
          </Box>
          <Box sx={noPlanBodySx}>
            {`Looks like your last subscription ended on `}
            <Box
              component="span"
              sx={{ fontWeight: t.weight.strong, color: t.color.ink }}
            >
              {convertESTtoUserLocalTime(
                accountStats?.validTill,
                "YYYY-MM-DDTHH:mm:s",
                "Do MMM, YYYY",
              )}
            </Box>
            {`. Pick a plan to get back to creating, sending, and tracking with ease.`}
          </Box>
        </Stack>
        <Button
          disableElevation
          sx={accentButtonSx(t.size.buttonHeight, "18px")}
          onClick={() => setUpgradePlanScreen(true)}
        >
          Choose a plan
        </Button>
      </Stack>
    );
  };

  // ---- derived plan values (shared by the header + stat strip) ----
  const days = currentPlan?.validityInDays;
  const isAnnual = days === 365 || days === 366;
  // The plan price is per seat (per month); the total is that × users in account.
  // `currentPlan` (`GET .../plan/current`) doesn't reliably carry `discountPrice`
  // — same reason the seat cap below falls back to `catalogPlan` — so annual
  // pricing falls back to the matched catalog entry, which always has it.
  const basePrice =
    Number(currentPlan?.price) || Number(catalogPlan?.price) || 0;
  const discountPrice =
    Number(currentPlan?.discountPrice) || Number(catalogPlan?.discountPrice) || 0;
  const hasAnnualDiscount =
    isAnnual && discountPrice > 0 && discountPrice < basePrice;
  const perSeatMonthly = hasAnnualDiscount ? discountPrice : basePrice;
  // A free plan isn't billed on any cadence, so the Monthly/Annual/Forever
  // badge next to its name would just be noise.
  const cycle = basePrice > 0 ? getBillingCycleLabel(days) : null;
  const isSignUnlimited = isUnlimitedLimit(certifiedSignedStats?.limit);
  const signUsed = certifiedSignedStats?.current ?? 0;
  const signLimit = certifiedSignedStats?.limit ?? 0;
  const signPct = signLimit > 0 ? Math.round((signUsed / signLimit) * 100) : 0;

  /**
   * Every figure on this card comes from the billing summary, which is the one
   * place that separates the three numbers: `seats` bought (the billing basis),
   * `activeUsers` filling them, and `cap` (the most buyable — a sentinel like
   * 99999 on an uncapped plan, so it must never reach a price or a meter).
   *
   * `planLimit` is the fallback for accounts the summary won't answer for.
   */
  const seatUsage = getSeatUsage(accountStats);
  const usersInAccount = billingSummary?.activeUsers ?? seatUsage.used;
  const seatLimit = billingSummary?.seats ?? seatUsage.limit;
  const seatsMain =
    seatLimit > 0 && !isUnlimitedLimit(seatLimit)
      ? `${usersInAccount} of ${seatLimit}`
      : `${usersInAccount}`;

  // Only per-seat plans multiply by the purchased seat count; everything else
  // bills a flat price at quantity 1 (§7).
  const billedSeats = getBilledSeatCount(seatLimit, isPerSeat);
  const localTotal =
    (isAnnual ? perSeatMonthly * 12 : perSeatMonthly) * billedSeats;
  // The summary's subtotal is `seats × unitPrice` verbatim — correct for a
  // monthly plan, but on an annual one `unitPrice` comes back as the
  // undiscounted *monthly* rate with no ×12 (the same backend bug documented
  // on `PlanSwitchPreview`/`AddOnInvoicePreview` in `types/perSeat.ts`), so
  // annual always uses the locally computed, discounted total instead.
  //
  // A summary that carries `discountedSubtotal` is priced off Stripe's
  // upcoming invoice instead: discounts applied and annual amounts real, so
  // it is trusted on every cycle.
  const isInvoiceSummary =
    billingSummary?.discountedSubtotal !== undefined &&
    billingSummary?.discountedSubtotal !== null;
  const trustSummaryPrice = !isAnnual || isInvoiceSummary;
  const cycleTotal =
    trustSummaryPrice && billingSummary
      ? billingSummary.discountedSubtotal ?? billingSummary.subtotal
      : localTotal;
  /** A discount on the subscription itself, e.g. bought with a sale code. */
  const subscriptionDiscount =
    isInvoiceSummary && (billingSummary?.discountPercent ?? 0) > 0
      ? {
          percent: Number(billingSummary?.discountPercent),
          endsOn: billingSummary?.discountEndsOn
            ? moment(billingSummary.discountEndsOn, "YYYY-MM-DD")
            : null,
          regular: billingSummary?.regularAmountAfterDiscount ?? null,
        }
      : null;
  const originalTotal =
    (isAnnual ? basePrice * 12 : basePrice) * billedSeats;
  // An invoice-priced summary already holds the real annual bill, so the
  // saving vs monthly is read off it rather than the catalog's
  // `discountPrice` — during a sale that field holds the sale price.
  const yearlySavings = isInvoiceSummary
    ? isAnnual
      ? Math.max(0, originalTotal - cycleTotal)
      : 0
    : hasAnnualDiscount
      ? originalTotal - localTotal
      : 0;
  const annualSavingsPercent = isInvoiceSummary
    ? originalTotal > 0
      ? Math.round((yearlySavings / originalTotal) * 100)
      : 0
    : hasAnnualDiscount && basePrice > 0
      ? Math.round(((basePrice - discountPrice) / basePrice) * 100)
      : 0;
  const showsAnnualSaving = isInvoiceSummary
    ? yearlySavings > 0
    : hasAnnualDiscount;

  /**
   * What the next renewal actually charges. The billing summary is
   * authoritative for a monthly plan — it reads the live Stripe quantity and
   * nets off the credit sitting on the account. On an annual plan its
   * `nextRenewalAmount` carries the same undiscounted/un-annualized bug as
   * `subtotal` above, so this uses the locally computed total instead; that
   * local sum is also the fallback for accounts the endpoint won't answer for
   * at all. The seat preview is never used here: it quotes purchased + 1
   * seat, which is a different number.
   */
  const renewalTotal =
    trustSummaryPrice && billingSummary
      ? billingSummary.nextRenewalAmount
      : localTotal;
  const renewalCurrency = billingSummary?.currency ?? currentPlan?.currency;
  const creditBalance = billingSummary?.creditBalance ?? 0;
  const creditApplied = billingSummary?.creditApplied ?? 0;

  const renewalDate = billingSummary?.nextRenewalDate
    ? new Date(billingSummary.nextRenewalDate)
    : accountStats?.validTill
      ? new Date(accountStats.validTill)
      : null;
  const hasRenewalDate = Boolean(
    renewalDate && !Number.isNaN(renewalDate.getTime()),
  );
  const renewMain = hasRenewalDate
    ? `${renewalDate!.getDate()} ${renewalDate!.toLocaleDateString("en-US", {
        month: "short",
      })}`
    : "—";
  // With credit in play the bare figure looks wrong ("$0.00 due?"), so the
  // caption shows the arithmetic behind it: gross, less credit, net.
  // The subscription's discount only belongs on this renewal if it still
  // covers it: Stripe stops applying a coupon on its end date, so a renewal on
  // or after `discountEndsOn` is billed at the regular price. No end date
  // means a coupon that never ends.
  const discountCoversRenewal = Boolean(
    subscriptionDiscount &&
      hasRenewalDate &&
      (!subscriptionDiscount.endsOn ||
        moment(renewalDate!).isBefore(subscriptionDiscount.endsOn, "day")),
  );
  const annualDiscountNote = subscriptionDiscount
    ? discountCoversRenewal
      ? ` (${subscriptionDiscount.percent}% off)`
      : ""
    : showsAnnualSaving
      ? ` (${annualSavingsPercent}% off)`
      : "";
  const renewSub = !hasRenewalDate
    ? ""
    : creditApplied > 0
      ? `${renewalDate!.getFullYear()} · ${formatMoney(
          cycleTotal,
          renewalCurrency,
        )} − ${formatMoney(creditApplied, renewalCurrency)} credit = ${formatMoney(
          renewalTotal,
          renewalCurrency,
        )}${annualDiscountNote}`
      : `${renewalDate!.getFullYear()} · ${formatMoney(
          renewalTotal,
          renewalCurrency,
        )}${annualDiscountNote}`;

  // The summary reports the seats Stripe actually bills, which is the number to
  // quote; the account's purchased count is the fallback when it isn't there.
  const quotedSeats = billingSummary?.seats ?? billedSeats;
  // Per seat, per billing cycle — annual always renders its own discounted
  // per-*year* price (matching `cycleTotal`/`localTotal` above), never the
  // summary's undiscounted monthly-scale `unitPrice`.
  // An invoice-priced summary's `unitPrice` is the list rate, so the per-seat
  // figure is read back off the (discounted) cycle total instead.
  const quotedUnitPrice =
    isInvoiceSummary && quotedSeats > 0
      ? cycleTotal / quotedSeats
      : trustSummaryPrice && billingSummary
      ? billingSummary.unitPrice
      : isAnnual
        ? perSeatMonthly * 12
        : perSeatMonthly;
  /** The most seats buyable — bounds the picker, nothing else. */
  const seatCap =
    billingSummary?.cap ??
    (getPlanSeatLimit(currentPlan) || getPlanSeatLimit(catalogPlan));

  /**
   * Price, renewal and seats all come from the summary, so they wait for it.
   * Rendering the `planLimit` fallback first makes the card visibly change its
   * own figures a moment later — worse than a brief skeleton, and it leaves the
   * reader unsure which set was true.
   */
  const isStatLoading = isLoading || (isSubscriptionActive && isCreditLoading);

  // A free plan (trial, lifetime free) isn't billed on any cadence, so
  // "billed monthly" is as much noise here as the header's cycle badge is.
  const billingSuffix =
    basePrice > 0 ? ` · billed ${(cycle || "monthly").toLowerCase()}` : "";
  // "60% off until 3 Feb 2027, then $175/mo" — how long the discount lasts.
  const discountCaption = subscriptionDiscount
    ? `${subscriptionDiscount.percent}% off${
        subscriptionDiscount.endsOn?.isValid()
          ? ` until ${subscriptionDiscount.endsOn.format("D MMM YYYY")}`
          : ""
      }${
        subscriptionDiscount.regular !== null
          ? `, then $${formatPlanAmount(
              subscriptionDiscount.regular,
            )}${getPeriodLabel(days)}`
          : ""
      }`
    : "";
  const seatsCaption =
    isPerSeat && !isUnlimitedLimit(quotedSeats)
      ? `${quotedSeats} ${
          quotedSeats === 1 ? "seat" : "seats"
        } × $${formatPlanAmount(quotedUnitPrice)}`
      : "";
  const priceCaption = subscriptionDiscount
    ? [seatsCaption, discountCaption].filter(Boolean).join(" · ")
    : showsAnnualSaving
    ? `You save $${yearlySavings.toFixed(2)}/yr vs monthly`
    : // An uncapped plan reports the sentinel as its seat count; quoting
      // "99999 seats × $0" is worse than saying nothing about quantity.
      isPerSeat && !isUnlimitedLimit(quotedSeats)
      ? `${quotedSeats} ${
          quotedSeats === 1 ? "seat" : "seats"
        } × $${formatPlanAmount(quotedUnitPrice)}${billingSuffix}`
      : basePrice > 0
        ? `billed ${(cycle || "monthly").toLowerCase()}`
        : "";

  const canManageBilling = hasAccessTo(
    strings.resources.billing,
    strings.updatePermission,
  );

  const renderStat = (
    label: string,
    value: React.ReactNode,
    caption: React.ReactNode,
    index: number,
  ) => (
    <Box sx={statCellSx(index, 4)}>
      <Box sx={statLabelSx}>{label}</Box>
      {value}
      {caption ? <Box sx={statCaptionSx}>{caption}</Box> : null}
    </Box>
  );

  return (
    <Box sx={planCardSx}>
      {!isLoading && isPlanExpired ? (
        getNoActivePlanBox()
      ) : (
        <>
          {/* Header: plan name + billing-cycle badge + actions */}
          {/*
            The reference stacks the plan name, the billing sentence and the
            action row below `md`; desktop keeps them on one line.
          */}
          <Box
            sx={{
              px: { xs: r.gutter, md: t.space.gutter },
              py: { xs: r.headerY, md: t.space.planHeaderY },
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: { xs: "stretch", md: "center" },
              gap: { xs: r.headerGap, md: "14px" },
              flexWrap: "wrap",
              borderBottom: `${t.border.hairline} solid ${t.color.rowDivider}`,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "3px",
                minWidth: 0,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                {isLoading ? (
                  <Skeleton width={160} height={28} />
                ) : (
                  <Box component="h2" sx={planNameSx}>
                    {currentPlan?.name ?? "No Plan"}
                  </Box>
                )}
                {!isLoading && cycle && (
                  <Box
                    component="span"
                    sx={pillSx({
                      color: t.color.cycleBadgeText,
                      bgcolor: t.color.cycleBadgeBg,
                      fontSize: t.font.cycleBadge,
                      letterSpacing: t.letterSpacing.cycleBadge,
                      paddingY: "3px",
                      uppercase: true,
                    })}
                  >
                    {cycle}
                  </Box>
                )}
              </Box>
              <Box component="span" sx={sectionDescSx}>
                {currentPlan?.description ??
                  "Your current subscription and usage."}
              </Box>
            </Box>

            {!isLoading && canManageBilling && (
              <Box
                sx={{
                  ml: { md: "auto" },
                  display: "flex",
                  // `row-reverse` puts "Change plan" first below `md`, as the
                  // reference does, without reordering the DOM.
                  flexDirection: { xs: "row-reverse", md: "row" },
                  justifyContent: { xs: "flex-end", md: "flex-start" },
                  gap: { xs: r.actionGap, md: "10px" },
                  flexShrink: 0,
                }}
              >
                {/* Seats are bought up front, so changing how many you pay for
                    is its own action — not a plan change and not a user
                    action. It belongs on the screen you land on after buying. */}
                {isPerSeat && isSubscriptionActive && (
                  <Button
                    disableElevation
                    sx={ghostButtonSx}
                    // The panel expands inside this card rather than opening a
                    // dialog, so the control toggles it and says which way.
                    onClick={() => setManagingSeats((open) => !open)}
                    aria-expanded={isManagingSeats}
                    // The panel opens seeded with the purchased count and cap —
                    // both still in flight while the summary loads, so it must
                    // not be clickable until they've actually landed.
                    disabled={isStatLoading}
                  >
                    {isManagingSeats
                      ? strings.manageSeats.closeLabel
                      : strings.manageSeats.toggleLabel}
                  </Button>
                )}
                {!isTrialAccount && isSubscriptionActive && (
                  <Button
                    disableElevation
                    sx={ghostButtonSx}
                    onClick={() => setCancelSubscriptionPopup(true)}
                    disabled={isStatLoading}
                  >
                    Cancel subscription
                  </Button>
                )}
                <Button
                  disableElevation
                  sx={planActionPrimarySx(t.size.buttonHeight, "18px")}
                  onClick={() => setUpgradePlanScreen(true)}
                  disabled={isStatLoading}
                >
                  Change plan
                </Button>
              </Box>
            )}
          </Box>

          {/* The old subscription doesn't come back on this screen — an
              expired paid plan auto-converts to a lifetime free one and only
              the plan currently in effect is reported, never the cancelled
              one — so this says what happened rather than showing a plan
              that no longer exists. */}
          {!isLoading &&
            subscriptionState?.state === subscriptionStates.cancelled && (
              <Box sx={cancelledBannerSx}>
                <Box component="span" sx={cancelledTitleSx}>
                  Plan cancelled
                </Box>
                <Box component="span" sx={cancelledNoteSx}>
                  {/*
                    No date here at all. The banner's job is to say the paid
                    plan ended and which one took over; the `validTill` that
                    comes back with it belongs to the *replacement* plan — the
                    free one — so the date either quotes the far-future
                    sentinel (3 Jun 2300) or reads as a deadline on a plan that
                    has none. When the plan in effect does expire, the card's
                    own NEXT RENEWAL stat is where that is reported.

                    The plan name comes from `currentPlan`, not
                    `subscriptionState.planName` — despite that field's own
                    contract ("the plan currently in effect"), it's been
                    observed still returning the just-cancelled plan's name
                    right after cancel, while `currentPlan` already reports
                    the replacement correctly in the same request batch.
                  */}
                  {`You've been moved to the ${currentPlan?.name ?? "Free"} plan.`}
                </Box>
              </Box>
            )}

          {/* The credit call runs after the plan card has rendered, so hold its
              place rather than letting a banner appear under the header a
              moment later and push the stats down. */}
          {!isLoading && isCreditLoading && isSubscriptionActive && (
            <Box sx={creditBannerSx}>
              <Skeleton width={120} height={24} />
              <Skeleton width="45%" height={18} />
            </Box>
          )}

          {/* Account credit. A downgrade or seat release leaves its money here
              rather than on the card, and Stripe spends it on later invoices by
              itself — so it stays visible until it runs out. */}
          {!isLoading && !isCreditLoading && creditBalance > 0 && (
            <Box sx={creditBannerSx}>
              <Box component="span" sx={creditAmountSx}>
                {`${formatMoney(creditBalance, renewalCurrency)} credit`}
              </Box>
              <Box component="span" sx={creditNoteSx}>
                {creditApplied > 0
                  ? `Applied automatically to your upcoming invoices — ${formatMoney(
                      creditApplied,
                      renewalCurrency,
                    )} of it covers your next renewal, leaving ${formatMoney(
                      creditBalance - creditApplied,
                      renewalCurrency,
                    )}.`
                  : "Applied automatically to your upcoming invoices."}
              </Box>
            </Box>
          )}

          {/* Stat strip */}
          <Box
            sx={{
              display: "grid",
              // Both small references lay the four stats out 2 x 2.
              gridTemplateColumns: {
                xs: "1fr 1fr",
                md: t.grid.statStrip,
              },
            }}
          >
            {renderStat(
              "PRICE",
              isStatLoading ? (
                <Skeleton width={110} height={30} />
              ) : (
                <Box sx={statValueSx(t.color.ink)}>
                  {/* The per-cycle bill: seats BOUGHT × unit price. Never the
                      cap, which on an uncapped plan is a sentinel. */}
                  {`$${formatPlanAmount(cycleTotal)}`}
                  <Box component="span" sx={statUnitSx}>
                    {getPeriodLabel(days)}
                  </Box>
                </Box>
              ),
              isStatLoading ? <Skeleton width="70%" height={16} /> : priceCaption,
              0,
            )}
            {renderStat(
              "NEXT RENEWAL",
              // Both the date and the amount are the summary's, so the whole
              // stat waits rather than showing `validTill` and then moving it.
              isStatLoading ? (
                <Skeleton width={110} height={30} />
              ) : (
                <Box sx={statValueSx(t.color.ink)}>
                  {isSubscriptionActive ? renewMain : "—"}
                </Box>
              ),
              isStatLoading ? (
                <Skeleton width="80%" height={16} />
              ) : !isSubscriptionActive ? (
                "no upcoming charges"
              ) : renewalTotal === 0 && creditApplied > 0 ? (
                `${renewSub} — covered by your account credit`
              ) : (
                renewSub
              ),
              1,
            )}
            {renderStat(
              "SIGN REQUESTS",
              isLoading ? (
                <Skeleton width={110} height={30} />
              ) : isSignUnlimited ? (
                <Box sx={statValueSx(t.color.successText)}>∞</Box>
              ) : (
                <>
                  <Box sx={statValueSx(t.color.ink)}>
                    {signUsed}
                    <Box component="span" sx={statUnitSx}>
                      {` of ${signLimit} used`}
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      height: t.size.meterHeight,
                      borderRadius: t.radius.pill,
                      bgcolor: t.color.meterTrack,
                      mt: "2px",
                      overflow: "hidden",
                    }}
                  >
                    <Box
                      sx={{
                        width: `${Math.min(signPct, 100)}%`,
                        height: t.size.meterHeight,
                        borderRadius: t.radius.pill,
                        bgcolor:
                          signPct >= 80
                            ? t.color.meterFillHigh
                            : t.color.meterFill,
                      }}
                    />
                  </Box>
                </>
              ),
              isLoading
                ? null
                : isSignUnlimited
                  ? "unlimited, no monthly cap"
                  : `${signPct}% of your monthly allowance used`,
              2,
            )}
            {renderStat(
              "SEATS",
              isStatLoading ? (
                <Skeleton width={60} height={30} />
              ) : (
                <Box sx={statValueSx(t.color.ink)}>{seatsMain}</Box>
              ),
              isStatLoading
                ? <Skeleton width="70%" height={16} />
                : !isPerSeat
                  ? `included in your plan`
                  : isUnlimitedLimit(seatLimit)
                    ? // Nothing to count down from, so there is no "free"
                      // figure to report — only that the ceiling is absent.
                      `no seat limit on this plan`
                    : `${Math.max(0, seatLimit - usersInAccount)} free${
                        // The cap is the most they may buy — a note, never a
                        // number this card bills or meters against.
                        seatCap > 0 && !isUnlimitedLimit(seatCap)
                          ? ` · up to ${seatCap} on this plan`
                          : ""
                      }`,
              3,
            )}
          </Box>

          {/* Manage seats expands here, under the stats it changes — the same
              inline panel the SMS credits card uses for a purchase, not an
              overlay. Mounted only while open so each open starts from the
              seats the account currently owns. */}
          {isPerSeat && isSubscriptionActive && isManagingSeats && (
            <ManageSeatsPanel
              purchased={seatLimit}
              used={usersInAccount}
              maxSeats={seatCap}
              unitPrice={quotedUnitPrice}
              currency={renewalCurrency}
              // Fallback only, for the brief window before the seat preview
              // (priced for the exact chosen count) has resolved.
              nextRenewalDate={billingSummary?.nextRenewalDate}
              isSubmitting={isSeatUpdateRunning}
              onConfirm={runSeatUpdate}
              // Close on the way out, so returning from the upgrade view
              // doesn't drop the customer back into a half-filled panel.
              onUpgrade={() => {
                setManagingSeats(false);
                setUpgradePlanScreen(true);
              }}
            />
          )}
        </>
      )}
      <CustomSeatReductionDialog
        reduction={seatReduction}
        purchasedSeats={pendingSeatCount}
        isSubmitting={isSeatUpdateRunning}
        onConfirm={(keepUserIds) => runSeatUpdate(pendingSeatCount, keepUserIds)}
        onClose={() => setSeatReduction(null)}
      />

      <AppModal
        open={cancelSubscriptionPopup}
        onClose={handleClose}
        title="Cancel your subscription?"
        tone="destructive"
        size="md"
        busy={isLoading}
        primaryLabel="Yes"
        onPrimary={() =>
          cancelSubscription(setLoading, setCancelSubscriptionPopup, getUserData)
        }
        primaryLoading={isLoading}
        cancelLabel="No"
        onCancel={() => setCancelSubscriptionPopup(false)}
      >
        {cancelSubsPopupBody()}
      </AppModal>    </Box>
  );
};

export default PlanDetails;
